<template>
  <div class="rep-price-strip">
    <!-- Reserve — the one cell that changes color, because a rep moving the
         reserve mid-sale is the thing the clerk must not miss. -->
    <div class="rep-price-strip__cell rep-price-strip__cell--reserve" :class="{ 'rep-price-strip__cell--new': reserveIsNew }">
      <span class="rep-price-strip__cell-head">
        <span class="rep-price-strip__label">Reserve</span>
        <span v-if="reserveIsNew" class="rep-price-strip__new">New</span>
      </span>
      <span class="rep-price-strip__value">${{ (reserveValue || 0).toLocaleString() }}</span>
    </div>

    <div class="rep-price-strip__cell rep-price-strip__cell--book">
      <span class="rep-price-strip__label">Blk Book</span>
      <span class="rep-price-strip__value">${{ (blackBookValue || 0).toLocaleString() }}</span>
    </div>

    <div class="rep-price-strip__cell rep-price-strip__cell--proxy">
      <span class="rep-price-strip__cell-head">
        <span class="rep-price-strip__label">Proxy bids</span>
        <Button
          class="rep-price-strip__clear"
          type="button"
          icon="pi pi-times"
          text
          rounded
          title="Clear all proxy bids"
          @click="askToClearProxyBids"
        />
      </span>
      <span class="rep-price-strip__value">{{ proxyBidCount }}</span>
    </div>

    <Button
      class="rep-price-strip__reset"
      type="button"
      icon="pi pi-replay"
      label="Reset"
      title="Reset bidding back to asking"
      @click="askToResetBidding"
    />

    <!-- Both destructive actions confirm in place. A `Popover` rather than
         PrimeVue's ConfirmPopup service, which would make this component depend
         on the application having registered ConfirmationService — a global
         requirement nothing in its signature declares. -->
    <Popover ref="confirmation">
      <div class="rep-price-strip__confirm">
        <span class="rep-price-strip__confirm-message">
          <i class="pi pi-exclamation-triangle rep-price-strip__confirm-icon" />
          {{ pending?.message }}
        </span>
        <div class="rep-price-strip__confirm-actions">
          <Button class="rep-price-strip__confirm-cancel" label="Cancel" text @click="dismiss" />
          <Button class="rep-price-strip__confirm-accept" :label="pending?.accept" @click="accept" />
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import Button from 'primevue/button';
import Popover from 'primevue/popover';

// The strip above the action grid: what the lot must make, what the book says,
// and how many proxy bids are stacked behind it.
//
// It read all three through a composable and held the "has the rep moved the
// reserve since this lot took the block" comparison itself — which is state
// about a lot that outlives the render, and belongs with whatever is watching
// the lot. It is told now.
//
// The two destructive controls emit rather than calling the runtime, but they
// still confirm HERE. Clearing every proxy bid and winding a lot back to asking
// are both irreversible mid-sale, and the confirmation is part of the control,
// not of whatever the application does afterwards.
defineOptions({ name: 'RepPriceStrip' });

defineProps({
  // What the seller will let the lot go for.
  reserveValue: { type: Number, default: 0 },
  // The rep has changed it since this lot took the block.
  reserveIsNew: { type: Boolean, default: false },
  blackBookValue: { type: Number, default: 0 },
  proxyBidCount: { type: Number, default: 0 },
});

const emit = defineEmits(['clear-proxy-bids', 'reset-bidding']);

const confirmation = ref(null);
const pending = ref(null);

function ask(event, request) {
  pending.value = request;
  confirmation.value?.show(event);
}

const askToClearProxyBids = (event) =>
  ask(event, { message: 'Clear all proxy bids for this asset?', accept: 'Clear', event: 'clear-proxy-bids' });

const askToResetBidding = (event) =>
  ask(event, { message: 'Reset bidding back to asking for this asset?', accept: 'Reset', event: 'reset-bidding' });

function dismiss() {
  confirmation.value?.hide();
  pending.value = null;
}

function accept() {
  if (pending.value) emit(pending.value.event);
  dismiss();
}
</script>

<style scoped>
.rep-price-strip {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  gap: var(--p-rep-price-strip-gap);
}

.rep-price-strip__cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  gap: var(--p-rep-price-strip-cell-gap);
  padding: var(--p-rep-price-strip-cell-padding);
  background: var(--p-rep-price-strip-cell-background);
  border: var(--p-rep-price-strip-cell-border-width) solid var(--p-rep-price-strip-cell-border-color);
  border-radius: var(--p-rep-price-strip-cell-border-radius);
}

.rep-price-strip__cell--reserve {
  flex: 1.1;
  position: relative;
}

.rep-price-strip__cell--book {
  flex: 0.85;
}

.rep-price-strip__cell--proxy {
  flex: 0.7;
}

.rep-price-strip__cell--new {
  background: var(--p-rep-price-strip-new-background);
  border-color: var(--p-rep-price-strip-new-border-color);
  border-width: var(--p-rep-price-strip-new-border-width);
  box-shadow: var(--p-rep-price-strip-new-box-shadow);
}

.rep-price-strip__cell-head {
  display: inline-flex;
  align-items: center;
  gap: var(--p-rep-price-strip-cell-head-gap);
}

.rep-price-strip__label {
  text-transform: uppercase;
  font-size: var(--p-rep-price-strip-label-font-size);
  font-weight: var(--p-rep-price-strip-label-font-weight);
  letter-spacing: var(--p-rep-price-strip-label-letter-spacing);
  color: var(--p-rep-price-strip-label-color);
}

.rep-price-strip__cell--new .rep-price-strip__label {
  color: var(--p-rep-price-strip-new-label-color);
}

.rep-price-strip__value {
  font-variant-numeric: tabular-nums;
  font-family: var(--p-rep-price-strip-value-font-family);
  font-size: var(--p-rep-price-strip-value-font-size);
  font-weight: var(--p-rep-price-strip-value-font-weight);
  letter-spacing: var(--p-rep-price-strip-value-letter-spacing);
  line-height: var(--p-rep-price-strip-value-line-height);
  color: var(--p-rep-price-strip-value-color);
}

.rep-price-strip__cell--new .rep-price-strip__value {
  color: var(--p-rep-price-strip-new-value-color);
}

/* Pulses, because a reserve that moved while the clerk was looking elsewhere is
   the one thing on this strip they have to notice without being told. */
.rep-price-strip__new {
  text-transform: uppercase;
  animation: rep-price-strip-pulse 1.3s infinite;
  padding: var(--p-rep-price-strip-new-badge-padding);
  border-radius: var(--p-rep-price-strip-new-badge-border-radius);
  background: var(--p-rep-price-strip-new-badge-background);
  color: var(--p-rep-price-strip-new-badge-color);
  font-size: var(--p-rep-price-strip-new-badge-font-size);
  font-weight: var(--p-rep-price-strip-new-badge-font-weight);
  letter-spacing: var(--p-rep-price-strip-new-badge-letter-spacing);
}

.p-button.rep-price-strip__clear {
  width: 16px;
  height: 16px;
  padding: 0;
  color: var(--p-rep-price-strip-clear-color);
  font-size: var(--p-rep-price-strip-clear-font-size);
}

.p-button.rep-price-strip__clear:hover {
  background: transparent;
  color: var(--p-rep-price-strip-clear-hover-color);
}

.p-button.rep-price-strip__reset {
  flex: 0 0 96px;
  flex-direction: column;
  font-family: inherit;
  gap: var(--p-rep-price-strip-reset-gap);
  background: var(--p-rep-price-strip-reset-background);
  border: var(--p-rep-price-strip-reset-border-width) solid var(--p-rep-price-strip-reset-border-color);
  border-radius: var(--p-rep-price-strip-reset-border-radius);
  color: var(--p-rep-price-strip-reset-color);
  font-size: var(--p-rep-price-strip-reset-font-size);
  font-weight: var(--p-rep-price-strip-reset-font-weight);
  letter-spacing: var(--p-rep-price-strip-reset-letter-spacing);
  text-transform: uppercase;
}

.p-button.rep-price-strip__reset:hover {
  background: var(--p-rep-price-strip-reset-hover-background);
  border-color: var(--p-rep-price-strip-reset-border-color);
  color: var(--p-rep-price-strip-reset-color);
}

/* ── Confirmation ───────────────────────────────────────────────────────── */

.rep-price-strip__confirm {
  max-width: 260px;
  display: flex;
  flex-direction: column;
  gap: var(--p-rep-price-strip-confirm-gap);
}

.rep-price-strip__confirm-message {
  display: flex;
  align-items: flex-start;
  gap: var(--p-rep-price-strip-confirm-message-gap);
  font-size: var(--p-rep-price-strip-confirm-message-font-size);
  color: var(--p-rep-price-strip-confirm-message-color);
  line-height: var(--p-rep-price-strip-confirm-message-line-height);
}

.rep-price-strip__confirm-icon {
  color: var(--p-rep-price-strip-confirm-icon-color);
  font-size: var(--p-rep-price-strip-confirm-icon-font-size);
}

.rep-price-strip__confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--p-rep-price-strip-confirm-actions-gap);
}

.p-button.rep-price-strip__confirm-accept {
  background: var(--p-rep-price-strip-confirm-accept-background);
  border-color: var(--p-rep-price-strip-confirm-accept-border-color);
  color: var(--p-rep-price-strip-confirm-accept-color);
}

@keyframes rep-price-strip-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.85);
    opacity: 0.65;
  }
}

</style>
